import {
  Daily,
  Weekly,
  XHourly
} from '@dadohr/shared/domainTypes/adminSchedules';
import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import { FormControlLabel, MenuItem, Select } from '@material-ui/core';
import { FormControlLabelProps } from '@material-ui/core/FormControlLabel';
import React from 'react';
import { printTz } from '../../services/time';
import { SelectHour } from '../Time/SelectHour';
import { SelectWeekDay } from '../Time/SelectWeekDay';

type Freq = XHourly | Daily | Weekly;

type Props = {
  value: Freq;
  onChange: (nextValue: XHourly | Daily | Weekly) => void;
  readOnlyType?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
};

interface IOption {
  label: string;
  value: Freq['type'];
  interval?: number;
}

const OPTIONS: IOption[] = [
  {
    label: 'every hour',
    value: 'X_HOURLY',
    interval: 1
  },
  {
    label: 'every 3 hours',
    value: 'X_HOURLY',
    interval: 3
  },
  {
    label: 'daily',
    value: 'DAILY'
  },
  {
    label: 'weekly',
    value: 'WEEKLY'
  }
];

const TypeEdit: React.FC<{
  value: Freq;
  onChange: (option: IOption) => void;
  readOnly: boolean;
  disabled?: boolean;
}> = ({ value, onChange, readOnly, disabled }) => {
  // Find the index of the selected option
  const selectedIndex = OPTIONS.findIndex((o) => {
    if (o.value !== value.type) return false;
    if (o.value === 'X_HOURLY') {
      // For X_HOURLY, match interval 
      const interval = (value as XHourly).interval;
      return o.interval === interval;
    }
    // For other types, interval is not relevant
    return true;
  });

  return (
    <Select
      value={selectedIndex}
      variant="outlined"
      onChange={(ev) => {
        const option = OPTIONS[ev.target.value as number];
        onChange(option);
      }}
      readOnly={readOnly}
      disabled={disabled}
    >
      {OPTIONS.map((o, i) => (
        <MenuItem key={i} value={i}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  );
};

const changeType = (value: Freq, onChange: (nextValue: Freq) => void) => (
  option: IOption
) => {
  const type = option.value;
  if (type === 'WEEKLY') {
    onChange({
      ...value,
      type,
      weekDay: (value as Weekly).weekDay || 1
    } as Weekly);
  } else if (type === 'DAILY') {
    onChange({
      ...value,
      type
    } as Daily);
  } else {
    onChange({
      ...value,
      type: 'X_HOURLY',
      interval: option.interval
    } as XHourly);
  }
};

const changeHour = (value: Freq, onChange: (nextValue: Freq) => void) => (
  hour: number
) => {
  onChange({
    ...value,
    hour
  } as Freq);
};

const changeWeekday = (value: Freq, onChange: (nextValue: Freq) => void) => (
  weekDay: number
) => {
  onChange({
    ...value,
    weekDay
  } as Weekly);
};

export const Label: React.FC<{
  label: string;
  control: FormControlLabelProps['control'];
}> = ({ label, control }) => {
  const theme = useTheme();
  return (
    <FormControlLabel
      label={label}
      labelPlacement="start"
      control={control}
      classes={{
        root: css({
          '&:first-of-type': {
            marginLeft: '0 !important'
          },
          span: {
            margin: `0 ${theme.spacing()}px !important`,
            fontSize: '1rem !important'
          }
        })
      }}
    />
  );
};

export const FrequencyEdit: React.FC<Props> = ({
  value,
  onChange,
  readOnlyType = false,
  style = {},
  disabled = false
}) => {
  return (
    <div style={style}>
      <Label
        label="Run"
        control={
          <TypeEdit
            readOnly={readOnlyType || disabled}
            value={value}
            onChange={changeType(value, onChange)}
            disabled={disabled}
          />
        }
      />

      {value.type === 'WEEKLY' && (
        <Label
          label="on"
          control={
            <SelectWeekDay
              value={value.weekDay}
              onChange={changeWeekday(value, onChange)}
              disabled={disabled}
            />
          }
        />
      )}
      <Label
        label={value.type === 'DAILY' ? 'at' : 'starting at'}
        control={
          <SelectHour
            value={value.hour}
            onChange={changeHour(value, onChange)}
            disabled={disabled}
          />
        }
      />
      <Label label={printTz(value.tz)} control={<div />} />
    </div>
  );
};
